import {
    ACCOUNTS_AND_CONTACTS_MANDATORY_FIELDS,
    ACCOUNTS_MANDATORY_FIELDS,
    CONTACTS_MANDATORY_FIELDS,
} from "app/pages/sales-intelligence/sub-modules/management/constants";
import type { ExportSetting } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import { getExportSettingSourceType } from "app/pages/sales-intelligence/sub-modules/management/utils/utils";

import type { CrmEnrichmentJobObject, CrmProvider, EnrichmentJobMappingDTO } from "../types/types";

const CONTACT_LINKEDIN_OPTIONAL_FIELDS = ["contact_linkedin_url"] as const;

export const getMandatoryFieldKeys = (
    crmProvider: CrmProvider,
    enrichmentObject: CrmEnrichmentJobObject,
): readonly string[] => {
    switch (enrichmentObject) {
        case "contact":
            return crmProvider === "hubspot" ? ACCOUNTS_AND_CONTACTS_MANDATORY_FIELDS : CONTACTS_MANDATORY_FIELDS;
        case "lead":
            return ACCOUNTS_AND_CONTACTS_MANDATORY_FIELDS;
        case "company":
            return ACCOUNTS_MANDATORY_FIELDS;
        default:
            return [];
    }
};

/** Mandatory Similarweb metadata keys that still show the CRM overwrite toggle in enrichment field mapping */
const MANDATORY_METADATA_KEYS_ALLOWING_OVERWRITE_TOGGLE: ReadonlySet<string> = new Set(["company_name"]);

export const isEnrichmentOverwriteSwitchLocked = (
    crmProvider: CrmProvider,
    enrichmentObject: CrmEnrichmentJobObject,
    field: string,
): boolean => {
    const mandatoryKeys = getMandatoryFieldKeys(crmProvider, enrichmentObject);
    if (!mandatoryKeys.includes(field)) {
        return false;
    }
    return !MANDATORY_METADATA_KEYS_ALLOWING_OVERWRITE_TOGGLE.has(field);
};

const getDefaultOptionalFields = (
    crmProvider: CrmProvider,
    enrichmentObject: CrmEnrichmentJobObject,
): readonly string[] => {
    if (enrichmentObject === "lead" || (enrichmentObject === "contact" && crmProvider === "hubspot")) {
        return CONTACT_LINKEDIN_OPTIONAL_FIELDS;
    }
    return [];
};

const resolveExternalField = (fieldKey: string, exportSettings?: readonly ExportSetting[]): string =>
    exportSettings?.find((s) => s.field === fieldKey)?.externalField ?? "";

const toDefaultMapping = (fieldKey: string): EnrichmentJobMappingDTO => ({
    externalField: "",
    overwrite: false,
    sourceType: getExportSettingSourceType(fieldKey),
    metadata: fieldKey,
});

export const getDefaultMappings = (
    crmProvider: CrmProvider,
    enrichmentObject: CrmEnrichmentJobObject,
    exportSettings?: readonly ExportSetting[],
): EnrichmentJobMappingDTO[] => {
    const mandatoryFieldKeys = getMandatoryFieldKeys(crmProvider, enrichmentObject);
    const optionalFieldKeys = getDefaultOptionalFields(crmProvider, enrichmentObject);

    return [
        ...mandatoryFieldKeys.map((key) => ({
            ...toDefaultMapping(key),
            externalField: resolveExternalField(key, exportSettings),
        })),
        ...optionalFieldKeys.map(toDefaultMapping),
    ];
};
